// src/pages/league-dashboard/LiveScoring.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Activity, Users, Tv, ChevronDown, ChevronUp, RefreshCw, BarChart3 } from 'lucide-react';
import { dynastyTheme, dynastyTokens } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import LeagueBanner from '../../components/league-dashboard/LeagueBanner';
import GameCardGrid from '../../components/live-scoring/GameCardGrid';
import Gamecast from '../../components/live-scoring/Gamecast';
import PlayerLink from '../../components/PlayerLink';
import { isPitcherSlot, isPitcherPosition } from '../../utils/playerClassification';
import { resolveStatWindow } from '../../services/tables/statRegistry';
// DD-STAT-CONSOLIDATION: live-scoring locked-rule logic is now the shared SSoT
// (services/tables/liveScoringRates) — desktop AND mobile import the SAME helpers.
import {
  seasonRate,
  formatRate,
  formatAvg,
  buildLiveScoringColumns,
  HITTING_CONTEXT,
  PITCHING_CONTEXT,
} from '../../services/tables/liveScoringRates';

const POLL_INTERVAL = 5000;

// Re-exported from the shared SSoT (liveScoringRates) so existing unit tests that
// import { seasonRate, formatRate } from this module keep resolving.
export { seasonRate, formatRate };

const LiveScoring = ({ league, leagueId, teams, userTeam, leagueBanner, bannerConfig }) => {
  const [activeTab, setActiveTab] = useState('team-stats');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [secondsAgo, setSecondsAgo] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  // Tab 1: Team Stats
  const [selectedTeamId, setSelectedTeamId] = useState(userTeam?.team_id || '');
  const [teamStats, setTeamStats] = useState(null);
  const [playerSubTab, setPlayerSubTab] = useState('hitting');

  // Tab 2: Live Standings
  const [standingsData, setStandingsData] = useState(null);

  // Tab 3: Box Scores / Gamecast
  const [gamesData, setGamesData] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [expandedGames, setExpandedGames] = useState({});

  // Tab 4: All Teams
  const [allTeamsData, setAllTeamsData] = useState(null);
  const [allTeamsHittingSort, setAllTeamsHittingSort] = useState({ key: null, dir: 'desc' });
  const [allTeamsPitchingSort, setAllTeamsPitchingSort] = useState({ key: null, dir: 'desc' });

  // Set default team when userTeam loads
  useEffect(() => {
    if (userTeam?.team_id && !selectedTeamId) {
      setSelectedTeamId(userTeam.team_id);
    }
  }, [userTeam]);

  // Stable fetch function — uses refs to avoid re-creating the interval on every state change
  const activeTabRef = useRef(activeTab);
  const selectedTeamIdRef = useRef(selectedTeamId);
  activeTabRef.current = activeTab;
  selectedTeamIdRef.current = selectedTeamId;

  const fetchData = useCallback(async (isRefresh = false) => {
    const tab = activeTabRef.current;
    const teamId = selectedTeamIdRef.current;
    if (!isRefresh) setLoading(true);
    try {
      if (tab === 'team-stats' && teamId) {
        const response = await leaguesAPI.getLiveScoringTeamStats(leagueId, teamId);
        if (response.success) setTeamStats(response);
      } else if (tab === 'standings') {
        const response = await leaguesAPI.getLiveScoringStandings(leagueId);
        if (response.success) setStandingsData(response);
      } else if (tab === 'box-scores') {
        const response = await leaguesAPI.getLiveScoringGames(leagueId);
        if (response.success) setGamesData(response);
      } else if (tab === 'all-teams') {
        const response = await leaguesAPI.getAllTeamsLiveStats(leagueId);
        if (response.success) setAllTeamsData(response);
      }
      setLastUpdated(new Date());
      setSecondsAgo(0);
    } catch (error) {
      console.error('Error fetching live scoring data:', error);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Initial fetch + polling (stable — only depends on leagueId)
  useEffect(() => {
    fetchData();
    pollRef.current = setInterval(() => fetchData(true), POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchData]);

  // Refetch when tab or team changes
  useEffect(() => {
    fetchData();
  }, [activeTab, selectedTeamId]);

  // Seconds-ago ticker
  useEffect(() => {
    tickRef.current = setInterval(() => setSecondsAgo(prev => prev + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const tabConfig = [
    { id: 'team-stats', label: 'Live Team Stats', icon: Users },
    { id: 'standings', label: 'Live Standings', icon: Activity },
    { id: 'box-scores', label: 'Live Box Scores', icon: Tv },
    { id: 'all-teams', label: 'All Teams', icon: BarChart3 }
  ];

  // ===== Player table renderer (shared by hitter and pitcher sub-tabs) =====
  const RATE_STATS = new Set(['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP']);
  const COUNTING_HIGHLIGHTS = { R: 'success', HR: 'primary', RBI: 'success', SB: 'success', W: 'success', SV: 'success', SO: 'success', K: 'success', QS: 'success', HD: 'success' };
  const ERROR_HIGHLIGHTS = new Set(['ER', 'BB', 'BS']);

  const renderPlayerTable = (players, totals, type) => {
    const isHitter = type === 'hitting';
    const statType = isHitter ? 'hitting' : 'pitching';
    const scoringCats = league?.league_settings?.scoring_categories;
    const leagueCats = isHitter
      ? (scoringCats?.hitting || ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'])
      : (scoringCats?.pitching || ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS']);

    // DD-STAT-CONSOLIDATION: columns via the shared SSoT builder (registry canonical
    // order, one column source). Highlight is presentational, added here.
    const contextKeys = isHitter ? HITTING_CONTEXT : PITCHING_CONTEXT;
    const statCols = buildLiveScoringColumns(leagueCats, statType, contextKeys).map((c) => ({
      ...c,
      highlight: c.isScoring ? (COUNTING_HIGHLIGHTS[c.abbrev] || (ERROR_HIGHLIGHTS.has(c.abbrev) ? 'error' : undefined)) : undefined,
    }));

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className={`${dynastyTheme.classes.bg.darkLighter} border-b ${dynastyTheme.classes.border.neutral}`}>
              <th className={`py-2 px-3 text-left ${dynastyTheme.classes.text.white} font-bold`}>Player</th>
              <th className={`py-2 px-2 text-center ${dynastyTheme.classes.text.neutralLight}`}>POS</th>
              <th className={`py-2 px-2 text-center ${dynastyTheme.classes.text.neutralLight}`}>Team</th>
              <th className={`py-2 px-2 text-left ${dynastyTheme.classes.text.neutralLight} min-w-[140px]`}>Game</th>
              {statCols.map(col => (
                <th key={col.abbrev} className={`py-2 px-2 text-center font-semibold ${col.isScoring ? dynastyTheme.classes.text.primary : 'text-neutral-400'} text-xs`}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Player rows */}
            {players?.map((player, idx) => (
              <tr
                key={player.player_id || idx}
                className={`border-b ${dynastyTheme.classes.border.neutral} hover:bg-black/20`}
                style={{ backgroundColor: idx % 2 === 0 ? dynastyTheme.tokens.colors.neutral[900] : dynastyTheme.tokens.colors.neutral[800] }}
              >
                <td className={`py-1.5 px-3 ${dynastyTheme.classes.text.white} font-medium`}>
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const gs = player.game_status || '';
                      const isLive = gs.includes('Top') || gs.includes('Bot');
                      const isFinal = gs.startsWith('Final');
                      // Bible §4.5.9.1: classify by occupied slot; the live feed
                      // carries roster_position (e.g. 'P_6'). Fall back to the
                      // primary-position predicate only when no slot is present.
                      const isPitcher = player.roster_position
                        ? isPitcherSlot(player.roster_position)
                        : isPitcherPosition(player.position);
                      const hasStats = isPitcher
                        ? (player.ip && player.ip !== '0.0' && player.ip !== 0)
                        : (player.ab > 0 || player.r > 0 || player.bb > 0 || player.sb > 0 || player.hbp > 0);

                      // Green = currently active (hitters in live game, pitchers currently on mound)
                      const isActive = isPitcher ? (isLive && player.is_current_pitcher) : (isLive && hasStats);
                      if (isActive) {
                        return <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" title="In game"></span>;
                      } else if (isFinal && hasStats) {
                        return <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" title="Game over"></span>;
                      } else if (isLive && hasStats && !isActive) {
                        return <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" title="Exited game"></span>;
                      }
                      return null;
                    })()}
                    <PlayerLink mlbId={player.player_id} name={player.name} style={{ fontWeight: 500 }} />
                    {player.is_probable_starter && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-blue-600 text-white font-bold flex-shrink-0" title="Projected Starter">SP</span>
                    )}
                  </div>
                </td>
                <td className={`py-1.5 px-2 text-center ${dynastyTheme.classes.text.neutralLight}`}>{player.roster_position ? player.roster_position.replace(/_\d+$/, '') : player.position}</td>
                <td className={`py-1.5 px-2 text-center ${dynastyTheme.classes.text.neutralLight}`}>{player.mlb_team}</td>
                <td className={`py-1.5 px-2 text-left text-[11px] ${getGameStatusColor(player.game_status)}`}>
                  {player.game_pk ? (
                    // Deep-link the Game cell to that game's box score (Live Box Scores tab).
                    <button
                      type="button"
                      onClick={() => { setSelectedGame(player.game_pk); setActiveTab('box-scores'); }}
                      className="hover:underline cursor-pointer text-left"
                      title="Open live box score"
                    >
                      {player.game_status}
                    </button>
                  ) : (
                    player.game_status
                  )}
                </td>
                {(() => {
                  const gs = player.game_status || '';
                  const gameActive = gs.includes('Top') || gs.includes('Bot') || gs.startsWith('Final');
                  return statCols.map(col => {
                    // Locked live-scoring rule (statRegistry SURFACE_DATASOURCE):
                    // player RATE cols = PURE SEASON rate (recomputed from emitted
                    // season components); COUNTING cols = today's DAY value.
                    const window = resolveStatWindow('live_scoring', 'player', col.isRate);
                    let val, display;
                    if (col.isRate) {
                      val = window === 'season' ? seasonRate(col.abbrev, player.season) : player[col.liveKey];
                      display = formatRate(col.abbrev, val);
                    } else {
                      val = player[col.liveKey];
                      display = val ?? 0; // day counting; 0 = no events today (honest)
                    }
                    const numVal = typeof val === 'number' ? val : 0;

                    let cellColor;
                    if (!col.isScoring) {
                      // Context stats: brighter when game is live/final, dim when not started
                      cellColor = gameActive ? 'text-neutral-200' : 'text-neutral-400';
                    } else if (col.highlight && numVal > 0) {
                      cellColor = dynastyTheme.classes.text[col.highlight];
                    } else {
                      cellColor = `${dynastyTheme.classes.text.white} font-semibold`;
                    }

                    return (
                      <td key={col.abbrev} className={`py-1.5 px-2 text-center ${cellColor}`}>
                        {display}
                      </td>
                    );
                  });
                })()}
              </tr>
            ))}
            {(!players || players.length === 0) && (
              <tr>
                <td colSpan={4 + statCols.length} className={`py-4 text-center ${dynastyTheme.classes.text.neutralLight}`}>
                  No {type} on roster
                </td>
              </tr>
            )}
            {/* Totals row at bottom */}
            {totals && players?.length > 0 && (
              <tr style={{ backgroundColor: dynastyTheme.tokens.colors.primaryLight }}>
                <td className={`py-2 px-3 font-bold ${dynastyTheme.classes.text.primary}`} colSpan={4}>Team Totals</td>
                {statCols.map(col => {
                  // Locked rule: TEAM TOTALS row is DAY throughout — day counting summed
                  // + rates from the day totals the backend already computes
                  // (hitter_totals/pitcher_totals), NOT season. (resolveStatWindow
                  // 'team_total' returns 'day' for both axes; asserted in the C2 test.)
                  const tv = totals[col.liveKey];
                  const display = col.isRate ? formatRate(col.abbrev, tv) : (tv ?? 0);
                  return (
                    <td key={col.abbrev} className={`py-2 px-2 text-center font-bold ${dynastyTheme.classes.text.white}`}>
                      {display}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
        {/* Status dot legend */}
        <div className={`flex items-center gap-4 px-3 py-1.5 border-t ${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`}>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-400"></span>
            <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>In Game</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
            <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>Game Over</span>
          </div>
        </div>
      </div>
    );
  };

  // ===== Render helpers (inline JSX, NOT component functions) =====

  const renderTeamStatsTab = () => {
    if (!teamStats) return <LoadingSpinner />;

    const hitterCount = teamStats.hitting?.length || 0;
    const pitcherCount = teamStats.pitching?.length || 0;

    const subTabs = [
      { id: 'hitting', label: `Hitters (${hitterCount})` },
      { id: 'pitching', label: `Pitchers (${pitcherCount})` },
      { id: 'all', label: 'All' },
    ];

    return (
      <div className="space-y-4">
        <div className={dynastyTheme.components.card.base}>
          <div className={`flex items-center gap-2 px-4 py-2 border-b ${dynastyTheme.classes.border.neutral}`}>
            {subTabs.map(st => (
              <button
                key={st.id}
                onClick={() => setPlayerSubTab(st.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all
                  ${playerSubTab === st.id
                    ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                    : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                  }`}
              >
                {st.label}
              </button>
            ))}
          </div>

          {playerSubTab === 'hitting' && renderPlayerTable(teamStats.hitting, teamStats.hitter_totals, 'hitting')}
          {playerSubTab === 'pitching' && renderPlayerTable(teamStats.pitching, teamStats.pitcher_totals, 'pitching')}
          {playerSubTab === 'all' && (
            <div>
              {renderPlayerTable(teamStats.hitting, teamStats.hitter_totals, 'hitting')}
              <div className={`border-t-2 ${dynastyTheme.classes.border.neutral}`}></div>
              {renderPlayerTable(teamStats.pitching, teamStats.pitcher_totals, 'pitching')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStandingsTab = () => {
    if (!standingsData) return <LoadingSpinner />;

    const categories = standingsData.categories || { hitting: [], pitching: [] };
    const hittingCats = categories.hitting || [];
    const pitchingCats = categories.pitching || [];
    const allCats = [...hittingCats, ...pitchingCats];
    const rateCats = new Set(['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP']);
    const reversedCats = new Set(['ERA', 'WHIP', 'BAA', 'BB/9']);
    const numTeams = standingsData.teams?.length || 0;

    // Sort by total_points descending (API already sorts, but be explicit)
    const sorted = [...(standingsData.teams || [])].sort((a, b) => (b.total_points || 0) - (a.total_points || 0));

    // Pre-today points and daily change computed by the backend
    // (correctly handles rate stats from raw cumulative components)

    const formatRaw = (cat, val) => {
      if (val == null) return '-';
      if (rateCats.has(cat)) return typeof val === 'number' ? val.toFixed(3).replace(/^0/, '') : val;
      return val;
    };

    const getCatGlow = (team, cat, catPts) => {
      const pre = team.pre_today_category_points?.[cat];
      if (pre == null || catPts == null || catPts === '-' || pre === catPts) return {};
      if (catPts > pre) return { color: '#eab308', textShadow: '0 0 6px rgba(234,179,8,0.6), 0 0 12px rgba(234,179,8,0.3)' };
      return { color: '#ef4444', textShadow: '0 0 6px rgba(239,68,68,0.6), 0 0 12px rgba(239,68,68,0.3)' };
    };

    return (
      <div className={dynastyTheme.components.card.base}>
        <div className={`flex items-center gap-2 px-4 py-2 border-b ${dynastyTheme.classes.border.neutral}`}>
          <Activity className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>Live Standings</h3>
          <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight} ml-auto`}>
            Includes today's live stats
          </span>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={`${dynastyTheme.classes.bg.darkLighter} border-b ${dynastyTheme.classes.border.neutral}`}>
                <th className={`py-1 px-2 text-left ${dynastyTheme.classes.text.white} font-bold min-w-[170px]`}>Team</th>
                {hittingCats.map(cat => (
                  <th key={cat} className={`py-1 px-1 text-center ${dynastyTheme.classes.text.primary} font-semibold min-w-[44px] text-[10px]`}>{cat}</th>
                ))}
                <th className="w-[2px] p-0 bg-neutral-600"></th>
                {pitchingCats.map(cat => (
                  <th key={cat} className={`py-1 px-1 text-center ${dynastyTheme.classes.text.primary} font-semibold min-w-[44px] text-[10px]`}>{cat}</th>
                ))}
                <th className={`py-1 px-2 text-center ${dynastyTheme.classes.text.primary} font-bold min-w-[44px]`}>PTS</th>
                <th className={`py-1 px-2 text-center ${dynastyTheme.classes.text.neutralLight} font-medium min-w-[44px] text-[10px]`}>+/-</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((team, idx) => {
                const isUser = team.team_id === userTeam?.team_id;
                const bgColor = isUser ? dynastyTheme.tokens.colors.primaryLight : (idx % 2 === 0 ? dynastyTheme.tokens.colors.neutral[900] : dynastyTheme.tokens.colors.neutral[800]);
                const pts = team.total_points ?? 0;
                const dailyChange = team.daily_change ?? 0;

                return (
                  <tr key={team.team_id} className={`border-b ${dynastyTheme.classes.border.neutral}`} style={{ backgroundColor: bgColor }}>
                    <td className={`py-1.5 px-2 ${isUser ? dynastyTheme.classes.text.primary + ' font-bold' : dynastyTheme.classes.text.white}`}>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>{idx + 1}</span>
                        <span
                          className="truncate"
                          style={{ cursor: 'pointer' }}
                          onClick={() => { setActiveTab('team-stats'); setSelectedTeamId(team.team_id); }}
                        >{team.team_name}</span>
                      </div>
                      {team.owner_name && (
                        <div className={`text-[10px] ${dynastyTheme.classes.text.neutralLight} ml-4`}>{team.owner_name}</div>
                      )}
                    </td>
                    {hittingCats.map(cat => {
                      const catPts = team.category_points?.[cat] ?? '-';
                      const raw = team.stats?.[cat];
                      const glow = getCatGlow(team, cat, catPts);
                      const clr = Object.keys(glow).length > 0 ? '' : (
                        catPts === numTeams ? dynastyTheme.classes.text.success
                        : catPts === 1 ? dynastyTheme.classes.text.error
                        : dynastyTheme.classes.text.white
                      );
                      return (
                        <td key={cat} className={`py-1.5 px-1 text-center`}>
                          <div className={`text-[12px] font-medium ${clr}`} style={glow}>{formatPts(catPts)}</div>
                          <div className="text-[9px] text-neutral-500">{formatRaw(cat, raw)}</div>
                        </td>
                      );
                    })}
                    <td className="w-[2px] p-0 bg-neutral-600"></td>
                    {pitchingCats.map(cat => {
                      const catPts = team.category_points?.[cat] ?? '-';
                      const raw = team.stats?.[cat];
                      const glow = getCatGlow(team, cat, catPts);
                      const clr = Object.keys(glow).length > 0 ? '' : (
                        catPts === numTeams ? dynastyTheme.classes.text.success
                        : catPts === 1 ? dynastyTheme.classes.text.error
                        : dynastyTheme.classes.text.white
                      );
                      return (
                        <td key={cat} className={`py-1.5 px-1 text-center`}>
                          <div className={`text-[12px] font-medium ${clr}`} style={glow}>{formatPts(catPts)}</div>
                          <div className="text-[9px] text-neutral-500">{formatRaw(cat, raw)}</div>
                        </td>
                      );
                    })}
                    <td className={`py-1.5 px-2 text-center font-bold text-sm ${dynastyTheme.classes.text.primary}`}>
                      {formatPts(pts)}
                    </td>
                    <td className={`py-1.5 px-2 text-center text-xs font-semibold ${
                      dailyChange === 0 ? 'text-neutral-500'
                      : dailyChange > 0 ? dynastyTheme.classes.text.success
                      : dynastyTheme.classes.text.error
                    }`}>
                      {dailyChange === 0 ? '\u2014' : (dailyChange > 0 ? '+' : '') + dailyChange.toFixed(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderAllTeamsTab = () => {
    if (!allTeamsData) return <LoadingSpinner />;

    const scoringCats = league?.league_settings?.scoring_categories;
    const hCats = scoringCats?.hitting || ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'];
    const pCats = scoringCats?.pitching || ['W', 'SV', 'ERA', 'WHIP', 'SO', 'QS'];

    const renderTeamTable = (title, cats, statKey, contextCols, sortState, setSortState) => {
      const scoringSet = new Set(cats.map(c => c.toLowerCase()));
      const allCols = [...contextCols, ...cats.filter(c => !contextCols.includes(c))];

      const handleSort = (cat) => {
        const key = cat.toLowerCase();
        if (sortState.key === key) {
          setSortState({ key, dir: sortState.dir === 'desc' ? 'asc' : 'desc' });
        } else {
          // ERA/WHIP: lower is better, default asc. Everything else: default desc.
          const defaultDir = ['ERA', 'WHIP'].includes(cat) ? 'asc' : 'desc';
          setSortState({ key, dir: defaultDir });
        }
      };

      // Sort teams
      const teams = [...(allTeamsData.teams || [])];
      if (sortState.key) {
        teams.sort((a, b) => {
          let aVal = (a[statKey] || {})[sortState.key];
          let bVal = (b[statKey] || {})[sortState.key];
          // Treat '-' and null as worst
          if (aVal === '-' || aVal == null) aVal = sortState.dir === 'desc' ? -Infinity : Infinity;
          if (bVal === '-' || bVal == null) bVal = sortState.dir === 'desc' ? -Infinity : Infinity;
          if (typeof aVal === 'string') aVal = parseFloat(aVal) || 0;
          if (typeof bVal === 'string') bVal = parseFloat(bVal) || 0;
          return sortState.dir === 'desc' ? bVal - aVal : aVal - bVal;
        });
      }

      return (
        <div className={`border ${dynastyTheme.classes.border.neutral} rounded-lg overflow-hidden`}>
          <div className={`px-4 py-2 ${dynastyTheme.classes.bg.darkLighter} border-b-2 border-yellow-500/40`}>
            <h3 className={`text-sm font-bold ${dynastyTheme.classes.text.primary}`}>{title}</h3>
          </div>
          {/* DD-STAT-CONSOLIDATION C4: horizontal scroll for the All-Teams tables */}
          <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={`${dynastyTheme.classes.bg.darkLighter} border-b ${dynastyTheme.classes.border.neutral}`}>
                <th className={`py-2 px-3 text-left ${dynastyTheme.classes.text.white} font-bold min-w-[160px]`}>Team</th>
                {allCols.map(cat => {
                  const key = cat.toLowerCase();
                  const isScoring = scoringSet.has(key);
                  const isSorted = sortState.key === key;
                  return (
                    <th key={cat}
                        onClick={() => handleSort(cat)}
                        className={`py-1.5 px-2 text-center font-semibold ${isScoring ? dynastyTheme.classes.text.primary : 'text-neutral-400'} text-xs cursor-pointer hover:text-yellow-300 select-none`}>
                      <div className="flex items-center justify-center gap-0.5">
                        {cat}
                        {isSorted ? (
                          sortState.dir === 'desc'
                            ? <ChevronDown className="w-3 h-3" />
                            : <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3 opacity-20" />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {teams.map((team, idx) => {
                const stats = team[statKey] || {};
                const isUser = team.team_id === userTeam?.team_id;
                return (
                  <tr key={team.team_id}
                      className={`border-b ${dynastyTheme.classes.border.neutral} hover:bg-white/5`}
                      style={{ backgroundColor: isUser ? dynastyTheme.tokens.colors.primaryLight : (idx % 2 === 0 ? dynastyTheme.tokens.colors.neutral[900] : dynastyTheme.tokens.colors.neutral[800]) }}>
                    <td className={`py-1 px-3 ${isUser ? `${dynastyTheme.classes.text.primary} font-bold` : dynastyTheme.classes.text.white} font-medium cursor-pointer`}
                        onClick={() => { setSelectedTeamId(team.team_id); setActiveTab('team-stats'); }}>
                      <div className="truncate font-semibold text-xs hover:underline">{team.team_name}</div>
                      {team.owner_name && <div className={`text-[10px] ${dynastyTheme.classes.text.neutralLight}`}>{team.owner_name}</div>}
                    </td>
                    {allCols.map(cat => {
                      const key = cat.toLowerCase();
                      const val = stats[key];
                      const isScoring = scoringSet.has(key);
                      const isRate = RATE_STATS.has(cat);
                      const display = isRate
                        ? (['AVG', 'OBP', 'SLG', 'OPS'].includes(cat) ? formatAvg(val) : (val === '-' || val == null ? '-' : typeof val === 'number' ? val.toFixed(2) : val))
                        : (val ?? 0);

                      const cellColor = isScoring
                        ? `${dynastyTheme.classes.text.white} font-semibold`
                        : 'text-neutral-400';

                      return (
                        <td key={cat} className={`py-1 px-2 text-center ${cellColor}`}>
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {renderTeamTable("Today's Hitting", hCats, 'hitting', ['AB', 'H', 'BB'], allTeamsHittingSort, setAllTeamsHittingSort)}
        {renderTeamTable("Today's Pitching", pCats, 'pitching', ['IP', 'H', 'R', 'ER', 'BB'], allTeamsPitchingSort, setAllTeamsPitchingSort)}
      </div>
    );
  };

  const renderBoxScoresTab = () => {
    // Gamecast detail view for a selected game
    if (selectedGame) {
      return <Gamecast gamePk={selectedGame} leagueId={leagueId} onBack={() => setSelectedGame(null)} />;
    }

    // Game card grid — click a card to open gamecast
    if (!gamesData) return <LoadingSpinner />;
    return <GameCardGrid games={gamesData?.games} onSelectGame={setSelectedGame} />;
  };

  return (
    <div className="space-y-4">
      {/* Banner */}
      <LeagueBanner
        leagueBanner={leagueBanner}
        title="Live Scoring"
        subtitle={`${league?.league_name} • ${league?.current_season || ''} Season`}
        minHeight="120px"
        bannerConfig={bannerConfig}
      />

      {/* Tabs + Last Updated */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {tabConfig.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-1.5
                  ${activeTab === tab.id
                    ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                    : `${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.neutralLight} hover:bg-gray-700`
                  }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className={`text-[11px] ${dynastyTheme.classes.text.neutralLight} flex items-center gap-1`}>
          <RefreshCw className="w-3 h-3" />
          {lastUpdated ? `Updated ${secondsAgo}s ago` : 'Loading...'}
        </div>
      </div>

      {/* Team Selector — lives OUTSIDE tab content so it never unmounts */}
      {activeTab === 'team-stats' && (
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <label className={`text-sm font-medium ${dynastyTheme.classes.text.neutralLight} block mb-2`}>
            Select Team
          </label>
          <select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg text-sm ${dynastyTheme.classes.bg.neutralDarker} ${dynastyTheme.classes.text.white} border ${dynastyTheme.classes.border.neutral}`}
          >
            {teams.map(team => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name}{team.team_id === userTeam?.team_id ? ' (You)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tab Content — rendered inline, not as component functions */}
      {loading && !teamStats && !standingsData && !gamesData ? (
        <LoadingSpinner />
      ) : (
        <>
          {activeTab === 'team-stats' && renderTeamStatsTab()}
          {activeTab === 'standings' && renderStandingsTab()}
          {activeTab === 'box-scores' && renderBoxScoresTab()}
          {activeTab === 'all-teams' && renderAllTeamsTab()}
        </>
      )}
    </div>
  );
};

// ===== Stable helper components (defined OUTSIDE the main component) =====

const LoadingSpinner = () => (
  <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
    <RefreshCw className={`w-6 h-6 mx-auto mb-3 animate-spin ${dynastyTheme.classes.text.primary}`} />
    <p className={dynastyTheme.classes.text.neutralLight}>Loading live data...</p>
  </div>
);

// formatAvg now lives in the shared SSoT (liveScoringRates) and is imported above;
// re-exported here for any unit test that targets it via this module.
export { formatAvg };

export function formatPts(val) {
  if (val === '-' || val == null) return '-';
  return val % 1 === 0 ? val : val.toFixed(1);
}

export function getGameStatusColor(status) {
  if (!status) return dynastyTheme.classes.text.neutralLight;
  if (status.startsWith('Final')) return dynastyTheme.classes.text.neutralLight;
  if (status.includes('Top') || status.includes('Bot')) return dynastyTheme.classes.text.success;
  if (status === 'No game today') return dynastyTheme.classes.text.neutralLight;
  return dynastyTheme.classes.text.white;
}

export default LiveScoring;
